/**
 * EoGuardrailsTour-specific types and step definitions.
 *
 * The EoGuardrailsTour demonstrates the E&O (Errors & Omissions) risk
 * protection system — real-time message scanning, severity-based warnings,
 * built-in coaching, and client notes for added context.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each EoGuardrailsTour step */
export type EoGuardrailsTourStepId =
  | 'eo_welcome'
  | 'highlight_type_high_risk'
  | 'highlight_warning_banner'
  | 'highlight_expand_details'
  | 'highlight_details_content'
  | 'eo_context_matters'
  | 'highlight_open_sidebar'
  | 'highlight_notes_section'
  | 'eo_complete';

/** Ordered list of all EoGuardrailsTour steps */
export const EO_GUARDRAILS_TOUR_STEPS: TourStep[] = [
  {
    id: 'eo_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_type_high_risk',
    type: 'highlight',
    targetSelector: '[data-tour-id="message-input"]',
    title: 'Try It — High Risk Phrase',
    content:
      'Type "you\'re covered" in the message box. This is one of the most common E&O risk phrases in insurance — it implies a coverage guarantee that may not be accurate.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElement: '[data-tour-id="guardrail-warning-banner"]',
  },
  {
    id: 'highlight_warning_banner',
    type: 'highlight',
    targetSelector: '[data-tour-id="guardrail-warning-banner"]',
    title: 'Real-Time Risk Detection',
    content:
      'A HIGH risk phrase was detected instantly as you typed. The warning appears in real time — but notice it doesn\'t block you from sending the message. Your team can still send if the context makes it appropriate.',
    placement: 'top',
  },
  {
    id: 'highlight_expand_details',
    type: 'highlight',
    targetSelector: '[data-tour-id="guardrail-warning-banner"]',
    title: 'Teachable Moments',
    content:
      'Click the arrow to expand the details. Each flagged phrase includes an explanation of why it\'s risky. This turns every warning into a coaching opportunity — your team learns proper compliance language as they work.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElement: '[data-tour-id="guardrail-details-expanded"]',
  },
  {
    id: 'highlight_details_content',
    type: 'highlight',
    targetSelector: '[data-tour-id="guardrail-details-expanded"]',
    title: 'Built-In Coaching',
    content:
      'Each match shows the exact phrase, its severity level (HIGH, MEDIUM, or LOW), and a clear explanation of the risk. Low-risk words like "always," "never," and "guaranteed" are also flagged — absolutes rarely apply in insurance. Your team builds better habits naturally.',
    placement: 'top',
  },
  {
    id: 'eo_context_matters',
    type: 'highlight',
    targetSelector: '[data-tour-id="message-input"]',
    title: 'Context Is Everything',
    content:
      'In E&O, context matters as much as wording. A phrase like "you\'re covered" might be perfectly fine when discussing an existing umbrella policy. That\'s why guardrails flag but never block — and why having client context readily available is essential.',
    placement: 'top',
  },
  {
    id: 'highlight_open_sidebar',
    type: 'highlight',
    targetSelector: '[data-tour-id="contact-panel-btn"]',
    title: 'Client Context',
    content:
      'Click this button to open the contact details sidebar where your team can access important client information right alongside the conversation.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="contact-sidebar-open"]',
  },
  {
    id: 'highlight_notes_section',
    type: 'highlight',
    targetSelector: '[data-tour-id="notes-section"]',
    title: 'Team Notes',
    content:
      'Anyone on your team can add notes about a client or conversation. Notes like "Client aware of flood zone coverage gap" or "Discussed umbrella options on 3/15" give everyone the context they need to communicate accurately — reducing E&O risk by ensuring no one is working blind.',
    placement: 'left',
  },
  {
    id: 'eo_complete',
    type: 'modal',
    title: '',
    content: '',
  },
];
